import { DeviceExperience, DeviceType, EnvironmentType } from "@obg/ngx.global";
import { LocalizedRoute } from "@obg/ngx.router";

import {
	AppSettings,
	AppStartupContext,
	Device as DeprecatedDevice,
	DeviceExperience as DeprecatedDeviceExperience,
	DeviceType as DeprecatedDeviceType,
	ObgConfigFeAdaptiveClient,
	ObgNodeContext,
	Platform,
	RemoteModuleConfig,
} from "./app-context.model";

// TODO: OBGB-12018 remove once old device is removed
function convertDevice(deviceType: DeviceType, deviceExperience: DeviceExperience, culture: string): DeprecatedDevice {
	const isNative = deviceExperience.toLowerCase() === Platform.Native.toLowerCase();
	const isMobile = deviceType.toLowerCase() !== Platform.Desktop.toLowerCase();
	return {
		culture,
		deviceType: deviceType as unknown as DeprecatedDeviceType,
		isNative,
		deviceExperience: deviceExperience as unknown as DeprecatedDeviceExperience,
		platform: isNative ? Platform.Native : isMobile ? Platform.Mobile : Platform.Desktop,
	};
}

function isNativeExperience(): boolean {
	try {
		const cookiestring = RegExp("X-OBG-EXPERIENCE=[^;]+").exec(document.cookie);
		JSON.parse(decodeURIComponent(cookiestring ? cookiestring.toString().replace(/^[^=]+./, "") : ""));
		return true;
	} catch {
		return false;
	}
}

export function getStartupContext(
	nodeContext: ObgNodeContext,
	config: ObgConfigFeAdaptiveClient<{ appSettings: AppSettings }>,
	gdprCountries: string[],
	localizedRoutes: Record<string, LocalizedRoute>,
	translatedExternalRoutes: Record<string, Record<string, { slug: string }>>,
	remoteModuleConfigByIdentifier: Record<string, RemoteModuleConfig>,
	optimizelyFeatureFlags: Record<string, any>
): AppStartupContext {
	const {
		disabledRoutes,
		brandName,
		preload,
		rum,
		defaultMarketCode,
		crawlableAlternateDomainMarkets,
		alternateDomains,
		environment,
		marketList,
		isLanguageCodeInUrlDisabled,
		isDefaultMarketCodeUrlDisabled,
		isDebug,
	} = config.appSettings;

	const {
		version,
		isCrawlableForCurrentMarket,
		isBot,
		isSiteMapCrawler,
		exposeObgState,
		detectedMarket,
		activeExperiments,
		ssrConfig,
		isPrimaryDomain,
		isDebugEnabled,
		countryCode,
		deviceType,
		deviceExperience,
	} = nodeContext;

	const clientMarket = marketList.find((m) => m.languageCode === detectedMarket.code)!;

	const isCookieConsentDisabled = !gdprCountries.includes(countryCode);

	// TODO: OBGB-12018 remove old device
	const oldDevice = convertDevice(deviceType, deviceExperience, clientMarket.culture);

	const device = {
		deviceType,
		deviceExperience: deviceExperience || DeviceExperience.Web,
	};

	config.core.appContext.assetBaseUri = "";
	config.core.appContext.sessionToken = "";
	config.core.market.languageCode = clientMarket.languageCode;

	const startupContext = {
		config,
		device,
		disabledRoutes,
		marketList,
		remoteModuleConfigByIdentifier,
		appContext: {
			environment: environment as EnvironmentType,
			isDebug: isDebugEnabled || isDebug,
			version,
			countryCode,
			device: oldDevice,
			// TODO: OBGB-12018 remove device type and experience
			deviceType:
				oldDevice?.deviceType === DeprecatedDeviceType.Desktop
					? DeviceType.Desktop
					: oldDevice?.deviceType === DeprecatedDeviceType.Tablet
						? DeviceType.Tablet
						: DeviceType.Mobile,
			deviceExperience: device.deviceExperience,
		},
		rum,
		isBot,
		isSiteMapCrawler,
		isPrimaryDomain,
		blockDomainMarkets: crawlableAlternateDomainMarkets,
		alternateDomains,
		isCrawlableForCurrentMarket,
		activeExperiments: [
			"ssrClientConfiguration",
			...activeExperiments.map((experiment) => experiment.replace(/[^a-z-_]/gi, "")),
		],
		routes: localizedRoutes,
		translatedExternalRoutes,
		market: clientMarket,
		brandName: brandName.toLowerCase(),
		isCookieConsentDisabled,
		prefetchData: {},
		exposeObgState,
		isLanguageCodeInUrlDisabled,
		isCurrentMarketDefaultMarket: isDefaultMarketCodeUrlDisabled && defaultMarketCode === detectedMarket?.code,
		isReducedFunctionality: false,
		isSsrEnabled: ssrConfig.enabled,
		ssrConfig,
		preload,
		optimizelyFeatureFlags,
		environment,
	};

	// TODO: OBGB-11723 - Revert this commit and implement approach described in task
	if (isNativeExperience()) {
		startupContext.appContext.deviceExperience = DeviceExperience.Native;
		if (startupContext.appContext.device) {
			startupContext.appContext.device.deviceExperience = DeprecatedDeviceExperience.Native;
			startupContext.appContext.device.isNative = true;
		}
	}

	return startupContext;
}
